import React from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useNavigate } from "react-router-dom";

import { PrimaryButton, SecondaryButton, TitleText } from "../../common/native";
import { ADMIN_TOKENS } from "../../../styles/adminTokens";

type AdminLinkItem = {
  title: string;
  description: string;
  path: string;
  buttonText: string;
};

const TOKENS = ADMIN_TOKENS;

const PRIMARY_LINKS: AdminLinkItem[] = [
  {
    title: "Billing Admin",
    description:
      "Review billing status, plan mix, delinquent clinics, and clinic-level subscription and invoice history.",
    path: "/billing-admin",
    buttonText: "Open Billing Admin",
  },
  {
    title: "Admin Analytics",
    description:
      "Track global and per-clinic transform usage metrics, including simulations, transform images, and trend series by granularity.",
    path: "/admin-analytics",
    buttonText: "Open Admin Analytics",
  },
  {
    title: "Clinic Activity Stream",
    description:
      "Review individual clinic events across transforms, uploads, notes, audio, and side-by-side activity.",
    path: "/admin-activity-stream",
    buttonText: "Open Activity Stream",
  },
];

const OTHER_ADMIN_LINKS: AdminLinkItem[] = [
  {
    title: "Key Management",
    description: "Manage clinic API keys and key lifecycle controls.",
    path: "/key-management",
    buttonText: "Open Key Management",
  },
  {
    title: "Duplicate Images",
    description: "Review and resolve potential duplicate patient images.",
    path: "/duplicate-images",
    buttonText: "Open Duplicate Images",
  },
  {
    title: "Integrations",
    description: "Configure and monitor external integration connections.",
    path: "/integrations",
    buttonText: "Open Integrations",
  },
  {
    title: "Debug",
    description: "Internal debugging tools for diagnostics and experiments.",
    path: "/debug",
    buttonText: "Open Debug",
  },
  {
    title: "Before/After Modal Debug",
    description: "Dedicated debug page for before/after modal behavior.",
    path: "/before-after-modal-debug",
    buttonText: "Open Modal Debug",
  },
  {
    title: "CORS Debug",
    description: "Run sticker CORS diagnostics and export/download checks.",
    path: "/admin-management/cors-debug",
    buttonText: "Open CORS Debug",
  },
  {
    title: "Note Template Manager",
    description: "Manage note templates and navigation tooling.",
    path: "/note-template-manager",
    buttonText: "Open Template Manager",
  },
  {
    title: "Appointments",
    description:
      "Appointments workspace route (normally hidden in production nav).",
    path: "/appointments",
    buttonText: "Open Appointments",
  },
];

const AdminManagementPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.header}>
        <TitleText text="Admin Management Hub" />
        <Text style={styles.subtitle}>
          Central access point for internal admin workflows and dashboards.
        </Text>
      </View>

      <View style={styles.cardGrid}>
        {PRIMARY_LINKS.map((item) => (
          <View style={styles.card} key={item.path}>
            <Text style={styles.cardTitle}>{item.title}</Text>
            <Text style={styles.cardDescription}>{item.description}</Text>
            <PrimaryButton
              text={item.buttonText}
              onPress={() => navigate(item.path)}
              style={styles.cardAction}
            />
          </View>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Additional Admin Pages</Text>
        <Text style={styles.sectionDescription}>
          These routes were previously direct-link only and are now accessible
          here.
        </Text>
        <View style={styles.cardGrid}>
          {OTHER_ADMIN_LINKS.map((item) => (
            <View style={styles.card} key={item.path}>
              <Text style={styles.cardTitle}>{item.title}</Text>
              <Text style={styles.cardDescription}>{item.description}</Text>
              <SecondaryButton
                text={item.buttonText}
                onPress={() => navigate(item.path)}
                style={styles.cardAction}
              />
            </View>
          ))}
        </View>
      </View>

      <View style={styles.quickActions}>
        <SecondaryButton
          text="Go to Billing Admin"
          onPress={() => navigate("/billing-admin")}
          style={styles.quickButton}
        />
        <SecondaryButton
          text="Go to Admin Analytics"
          onPress={() => navigate("/admin-analytics")}
          style={styles.quickButton}
        />
        <SecondaryButton
          text="Go to Activity Stream"
          onPress={() => navigate("/admin-activity-stream")}
          style={styles.quickButton}
        />
        <SecondaryButton
          text="Go to Key Management"
          onPress={() => navigate("/key-management")}
          style={styles.quickButton}
        />
        <SecondaryButton
          text="Go to Integrations"
          onPress={() => navigate("/integrations")}
          style={styles.quickButton}
        />
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: TOKENS.color.background,
  },
  pageContent: {
    padding: TOKENS.spacing.xxl,
    gap: TOKENS.spacing.lg,
  },
  header: {
    gap: TOKENS.spacing.sm,
  },
  subtitle: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textSecondary,
    maxWidth: TOKENS.size.contentMaxWidth,
  },
  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: TOKENS.spacing.lg,
  },
  card: {
    flex: 1,
    minWidth: TOKENS.size.cardMinWidth,
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.xl,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.sm,
  },
  cardTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xl,
    color: TOKENS.color.textPrimary,
  },
  cardDescription: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSubtle,
    lineHeight: TOKENS.lineHeight.sm,
  },
  cardAction: {
    marginTop: TOKENS.spacing.sm,
  },
  quickActions: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.xl,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    flexWrap: "wrap",
  },
  quickButton: {
    minWidth: TOKENS.size.quickButtonMinWidth,
  },
  section: {
    gap: TOKENS.spacing.sm,
  },
  sectionTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xl,
    color: TOKENS.color.textPrimary,
  },
  sectionDescription: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSubtle,
  },
});

export default AdminManagementPage;
